import styled from '@emotion/styled';
import { Tooltip, Typography } from '@innovamat/glimmer-components';
import { useTranslation } from 'react-i18next';
import Score from '../Score';
import type { Block, StudentDrawerData } from './utils/types';

type Props = {
  data?: StudentDrawerData;
  drawerScoreData: Block[];
};

export function StudentDrawerScores({
  data,
  drawerScoreData,
}: Props): JSX.Element {
  const { t } = useTranslation();

  return (
    <Wrapper>
      {drawerScoreData.map((block) => (
        <BlockElement key={block.blockId}>
          <BlockHeader>
            <BlockTitle>
              <Typography.Subtitle1>
                {t(`${block.blockTitle?.text}`)}
              </Typography.Subtitle1>
              <Score score={block.score ?? null} type="box" />
            </BlockTitle>
          </BlockHeader>
          <ContentList>
            {block.contents?.map((content) => {
              return (
                <Content key={content?.contentId}>
                  <StyledTooltip
                    content={
                      t(`${content?.contentTitle?.text}`).length > 30
                        ? t(`${content?.contentTitle?.text}`)
                        : undefined
                    }
                  >
                    <ContentName>
                      {t(`${content?.contentTitle?.text}`)}
                    </ContentName>
                  </StyledTooltip>
                  <ContentScore>
                    <Score score={content?.score ?? null} type="underline" />
                  </ContentScore>
                </Content>
              );
            })}
          </ContentList>
        </BlockElement>
      ))}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const BlockElement = styled.div``;

const BlockTitle = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  gap: 16px;
`;

const BlockHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 48px;
  margin-bottom: 16px;
  border-bottom: 1px solid
    ${({ theme }) =>
      theme.tokens.color.specific.reports.tables['divider-primary'].value};
`;

const ContentList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-column-gap: 0;
  padding: 8px 0;
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Content = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 48px;
  min-width: 0;
  padding-right: 32px;
  margin-left: 32px;
  gap: 8px;
  border-right: 1px solid
    ${({ theme }) =>
      theme.tokens.color.specific.reports.tables['divider-secondary'].value};
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    &:nth-child(3n + 1) {
      margin-left: 0;
    }
    &:nth-child(3n) {
      border-right: none;
      margin-right: 0;
    }
    &:nth-last-child(-n + 3) {
      p,
      div {
        border-bottom: none;
      }
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    &:nth-child(2n + 1) {
      margin-left: 0;
    }
    &:nth-child(2n) {
      border-right: none;
      margin-right: 0;
    }
    &:nth-last-child(-n + 2) {
      * {
        border-bottom: none;
      }
    }
  }
`;

const StyledTooltip = styled(Tooltip)`
  height: 100%;
  width: 100%;
  min-width: 0;
`;

const ContentName = styled(Typography.Body2)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  height: 100%;
  min-width: 0;
  padding: 13px 0 0 8px;
  border-bottom: 1px solid
    ${({ theme }) =>
      theme.tokens.color.specific.reports.tables['divider-secondary'].value};
`;

const ContentScore = styled.div`
  padding: 8px 0;
  height: 100%;
  border-bottom: 1px solid
    ${({ theme }) =>
      theme.tokens.color.specific.reports.tables['divider-secondary'].value};
`;
